//#region imports

// react
import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';

// redux
import { useSelector, useDispatch } from "react-redux";
import { resetGetLocationsDataFlags, resetLocationData } from '_redux/slices/timeline/timeline-dashboard';
import { getAirportLocation, selectLiveAirportLocations, setPaxLocations } from '_redux/slices/timeline/timeline-areas';
import { selectFirebaseInfo } from '_redux/slices/main-screen/user';
import { selectAirportLocations, selectAllTimelineAreas } from '_redux/slices/timeline/timeline-areas';
import { setToast, setShowToast } from '_redux/slices/timeline/timeline-toast';

// firestore
import { firestore } from "_utils/firebase-app";
import { collection, onSnapshot, query, where } from "firebase/firestore";

// custom components
import NewAirportLocationCard from './new-airport-location-card';

// bootstrap
import { Col, Row } from 'react-bootstrap';

// styling
import { Spacing } from "_styles";

// icons
import AirportLocationsHeader from './airport-locations-header';

// virtualization
import { Virtuoso } from "react-virtuoso";
//#endregion

//#region Component
const NewAirportLocations = (props) => {

    //#region redux
    const dispatch = useDispatch();
    const liveAirportLocations = useSelector(selectLiveAirportLocations);
    const dbUser = useSelector(selectFirebaseInfo);
    //#endregion

    //#region local states
    const [searchTerm, setSearchTerm] = useState('');

    // Tracks report counts per RC session doc (by Firestore doc ID) so we can
    // detect when a passenger files a new report and fire a toast.
    const rcReportCountsRef = useRef({});
    const rcListenerMountedRef = useRef(false);
    //#endregion

    //#region useEffect

    // reset get location data flags and empty locationsData array on first render
    useEffect(() => {
        dispatch(resetLocationData());
        dispatch(resetGetLocationsDataFlags());
        dispatch(getAirportLocation(dbUser.details.authority));
    }, []);

    // subscribe to pax location changes
    useEffect(() => {
        const paxLocationsRef = collection(firestore, "paxLocations");

        const year = new Date().getFullYear();
        const month = String(new Date().getMonth() + 1).padStart(2, '0');
        const day = String(new Date().getDate()).padStart(2, '0');
        const locationsQuery = query(
            paxLocationsRef,
            where("iata", "==", dbUser.details.authority),
            where('paxDepartureDate', '==', `${year}-${month}-${day}`),
        );
        const unsubscribe = onSnapshot(locationsQuery, (snapshot) => {
            const updatedLocationsList = [];
            snapshot.forEach((doc) => {
                const locationsData = doc.data();
                updatedLocationsList.push(locationsData);
            });
            dispatch(setPaxLocations(updatedLocationsList));
        });

        return () => unsubscribe();
    }, []);

    // Listen for new RC session reports and fire a toast notification.
    // Watches remotecheckin_sessions for the current airport (non-revoked sessions only).
    // Suppresses toasts on the initial snapshot so only genuinely NEW reports trigger it.
    useEffect(() => {
        if (!dbUser?.details?.authority) return;

        const rcSessionsQuery = query(
            collection(firestore, 'remotecheckin_sessions'),
            where('iata', '==', dbUser.details.authority),
            where('revoked', '==', false),
        );

        const unsubscribeRC = onSnapshot(rcSessionsQuery, (snapshot) => {
            const isFirstLoad = !rcListenerMountedRef.current;

            snapshot.forEach((doc) => {
                const data = doc.data();
                const docId = doc.id;
                const reportCount = Array.isArray(data.reports) ? data.reports.length : 0;
                const prevCount = rcReportCountsRef.current[docId] ?? reportCount;

                if (!isFirstLoad && reportCount > prevCount) {
                    const passengerName = data.locationTitle || 'a passenger';
                    dispatch(setToast({
                        message: `New Remote Check-in Report - ${passengerName}`,
                        statusType: 'failed',
                    }));
                    dispatch(setShowToast(true));
                }

                rcReportCountsRef.current[docId] = reportCount;
            });

            if (isFirstLoad) rcListenerMountedRef.current = true;
        });

        return () => unsubscribeRC();
    }, [dbUser?.details?.authority]);

    // filter from locations array for matching locations
    const locations = useMemo(() => {

        if (!liveAirportLocations?.length) return [];

        let filteredPaxLocations = liveAirportLocations;

        // if search term exists, filter for matching pax locations
        if (searchTerm) {
            filteredPaxLocations = filteredPaxLocations?.filter(l => l?.locationName?.toLowerCase().includes(searchTerm?.toLowerCase()))
        }

        // sort the locations based on remoteCheckin
        return [...filteredPaxLocations].sort((a, b) => {
            const aHasRC = a.paxArray?.some(p => p?.paxManualCheckIn) ? 1 : 0;
            const bHasRC = b.paxArray?.some(p => p?.paxManualCheckIn) ? 1 : 0;
            return bHasRC - aHasRC;
        });

    }, [liveAirportLocations, searchTerm]);

    // component for virutualization
    const itemContent = useCallback((index) => {
        const location = locations[index];
        return (
            <div style={{ paddingBottom: Spacing.SCALE_8 }}>
                <NewAirportLocationCard location={location} />
            </div>
        )
    })

    useEffect(() => {
       //console.log('liveAirportLocations', liveAirportLocations)
    }, [liveAirportLocations])

    //#endregion

    return (
        <div style={{
            border: '1px solid lightgrey',
            height: '100%',
            padding: Spacing.SCALE_8,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
        }}
        >
            {/* search bar */}
            <div style={{ flexShrink: 0 }}>
                <AirportLocationsHeader setToggleAirportLocations={props.setToggleAirportLocations} setSearchTerm={setSearchTerm} />
            </div>

            {/* location cards - virtualized, fills remaining space */}
            <div className='mt-2' style={{ flex: 1, minHeight: 0 }}>
                {
                    !locations?.length && !searchTerm ? (
                        <div style={{ display:'flex', justifyContent:'center', alignItems:'center', height:'100%' }} className='mt-5'>
                            <span style={{ fontSize:'14px', justifyContent:'center', alignItems:'center', color:'#666', fontWeight:'500' }}>No Active Locations</span>
                        </div>
                    )
                        : !locations?.length && searchTerm ? (
                            <div className='d-flex align-items-center justify-content-center mt-2' style={{ fontSize: '12px', color: '#999' }}>No Locations Found Matching Search</div>

                        )
                            :
                            <Virtuoso
                                style={{ height: '100%' }}
                                data={locations}
                                overscan={100}
                                itemContent={itemContent}
                            />
                }

            </div>
        </div >
    );
};

export default NewAirportLocations;